import cx from 'classnames';
import i18next, { t } from 'i18next';
import { setPathLocale } from '../../common/localeShared';
import { asset } from '../services/assetResolverService';
import { navigateTo } from '../libs/history';
import { getTrackedInternalLinkUrl } from '../libs/tracking';
import { useTrackedImpression } from '../scripts/hooks/useTrackedImpression';

export type TZoneBlockerPleaLayout = 'portrait' | 'portraitCompact' | 'banner';

export interface ZoneBlockerPleaProps {
  width: number;
  height: number;
  layout: TZoneBlockerPleaLayout;
}

type TZoneBlockerPleaAction = 'premium' | 'allow_ads';

const LAYOUT_CLASS_NAMES: Record<TZoneBlockerPleaLayout, string> = {
  portrait: 'portrait',
  portraitCompact: 'portrait-compact',
  banner: 'banner'
};

const BACKGROUND_IMAGES: Record<TZoneBlockerPleaLayout, string> = {
  portrait: '/images/site/verts/zoneblock_bg_portrait.jpg?2026033106',
  portraitCompact: '/images/site/verts/zoneblock_bg_mrec.jpg?2026033110',
  banner: '/images/site/verts/zoneblock_bg_landscape.jpg?2026033106'
};

export function ZoneBlockerPlea({ width, height, layout }: ZoneBlockerPleaProps) {
  const isRetail = RIO_SITE.game === 'wowretail';
  const layoutClassName = `zone-blocker-plea--${LAYOUT_CLASS_NAMES[layout]}`;
  const size = `${width}x${height}`;
  const premiumLabel = t('upsells:adblock.cta-premium');
  const allowAdsLabel = t('upsells:adblock.cta-allow');
  const impressionRef = useTrackedImpression<HTMLDivElement>({
    onImpression: () => {
      RIO_TRACK_EVENT('event', 'zone_blocker_impression', {
        event_category: 'adblock',
        content_type: 'zone_blocker',
        content_id: `${layout}:${size}`,
        zone_blocker_layout: layout,
        zone_blocker_size: size
      });
    }
  });

  const buildTrackedUrl = (path: string, action: TZoneBlockerPleaAction) => {
    const params = new URLSearchParams({
      utm_source: 'raiderio',
      utm_medium: 'zoneblocker',
      utm_campaign: 'adblock',
      utm_content: `${action}_${layout}_${size}`
    });

    return `${path}?${params.toString()}`;
  };

  const trackClick = (action: TZoneBlockerPleaAction, url: string, linkText: string) => {
    RIO_TRACK_EVENT('event', 'zone_blocker_click', {
      event_category: 'adblock',
      content_type: 'zone_blocker',
      content_id: `${action}:${layout}:${size}`,
      destination_path: url,
      zone_blocker_action: action,
      zone_blocker_layout: layout,
      zone_blocker_size: size
    });

    if (isRetail && typeof window !== 'undefined') {
      RIO_TRACK_EVENT('event', 'internal_click', {
        link_url: getTrackedInternalLinkUrl(url),
        link_text: linkText,
        link_classes: `zone-blocker-plea ${layoutClassName}`,
        link_id: `zone_blocker:${action}:${layout}:${size}`
      });
    }
  };

  const buildHref = (path: string, action: TZoneBlockerPleaAction) => {
    const url = buildTrackedUrl(path, action);
    const localizedUrl = setPathLocale(url, i18next.language);
    return isRetail ? localizedUrl : `https://raider.io${localizedUrl}`;
  };

  const navigateToPath = (path: string, action: TZoneBlockerPleaAction, linkText: string) => {
    const url = buildTrackedUrl(path, action);
    const localizedUrl = setPathLocale(url, i18next.language);
    trackClick(action, url, linkText);

    if (isRetail) {
      navigateTo(url);
    } else {
      window.location.href = `https://raider.io${localizedUrl}`;
    }
  };

  const isModifiedClick = (e: React.MouseEvent) => {
    return e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey;
  };

  const handlePremiumNavigate = () => {
    navigateToPath('/premium', 'premium', premiumLabel);
  };

  const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof Element && e.target.closest('a')) {
      return;
    }

    handlePremiumNavigate();
  };

  const handlePremiumLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isModifiedClick(e)) {
      return;
    }

    e.preventDefault();
    handlePremiumNavigate();
  };

  const handleAllowAdsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isModifiedClick(e)) {
      return;
    }

    e.preventDefault();
    navigateToPath('/news/342-how-to-unblock-ads-on-raiderio', 'allow_ads', allowAdsLabel);
  };

  return (
    <div
      ref={impressionRef}
      className={cx('zone-blocker-plea', layoutClassName, 'rio-hidden--small')}
      data-testid="zone-blocker-plea"
      data-layout={layout}
      onClick={handleWrapperClick}
      style={{
        backgroundImage: `url(${asset(BACKGROUND_IMAGES[layout])})`,
        width,
        height
      }}
    >
      <div className="zone-blocker-plea__content">
        <div className="zone-blocker-plea__brand">
          <img
            className="zone-blocker-plea__logo"
            src={asset('/images/brand/Logo_2ColorWhite.png')}
            alt="Raider.IO"
          />
        </div>

        <div className="zone-blocker-plea__copy">
          <h3 className="zone-blocker-plea__title">
            {t('upsells:adblock.title')}
          </h3>

          <p className="zone-blocker-plea__body">
            {t('upsells:adblock.body')}
          </p>
        </div>

        <div className="zone-blocker-plea__actions">
          <a
            href={buildHref('/premium', 'premium')}
            className="zone-blocker-plea__cta-primary"
            onClick={handlePremiumLinkClick}
          >
            {premiumLabel}
          </a>

          <a
            href={buildHref('/news/342-how-to-unblock-ads-on-raiderio', 'allow_ads')}
            className="zone-blocker-plea__cta-secondary"
            onClick={handleAllowAdsClick}
          >
            {allowAdsLabel}
          </a>
        </div>
      </div>
    </div>
  );
}
